// Story 5.6 — TanStack Query READ hook for GET
// /api/v1/marketplace/me/sealed-dossier-listings.
//
// Sibling to useMyShareListings — same shape with the asset surface swap.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

import type { MyListingStatus } from "./useMyShareListings";

export interface MySealedDossierListingsParams {
  page: number;
  per_page: number;
  status?: MyListingStatus;
}

export interface MySealedDossierListingDossierData {
  sealed_dossier_id: string;
  drop_id: string;
  edition_number: number;
  portfolio_title: string;
  /** Wax-seal base color (migration 0079); null = default bordeaux. */
  seal_color: string | null;
  drop_status: string;
  drop_price_cents: number;
  drop_retiring_at: string | null;
}

export interface MySealedDossierListingRow {
  listing_id: string;
  status: string;
  price_cents: number;
  seller_anonymous: boolean;
  listed_at: string;
  expires_at: string | null;
  cancelled_at: string | null;
  sold_at: string | null;
  sold_to_user_id: string | null;
  /** Whether this listing accepts below-asking offers (Story 5.10). */
  allow_bidding: boolean;
  sealed_dossier: MySealedDossierListingDossierData;
}

export interface MySealedDossierListingsPageInfo {
  total_in_page: number;
  page: number;
  per_page: number;
  total_items: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface MySealedDossierListingsResponseData {
  data: MySealedDossierListingRow[];
  page_info: MySealedDossierListingsPageInfo;
}

async function fetchMySealedDossierListings(
  params: MySealedDossierListingsParams,
): Promise<MySealedDossierListingsResponseData> {
  const search = new URLSearchParams();
  search.set("page", String(params.page));
  search.set("per_page", String(params.per_page));
  if (params.status) {
    search.set("status", params.status);
  }
  const response = await fetch(
    `/api/v1/marketplace/me/sealed-dossier-listings?${search.toString()}`,
    {
      method: "GET",
      credentials: "include",
    },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(
      `Failed to load your sealed-dossier listings (HTTP ${response.status})`,
    );
  }
  const body = (await response.json()) as {
    data: MySealedDossierListingsResponseData;
  };
  return body.data;
}

export function useMySealedDossierListings(
  params: MySealedDossierListingsParams,
): UseQueryResult<MySealedDossierListingsResponseData, Error> {
  return useQuery<MySealedDossierListingsResponseData, Error>({
    queryKey: [
      "my-listings",
      "sealed_dossier",
      { page: params.page, per_page: params.per_page, status: params.status ?? null },
    ],
    queryFn: () => fetchMySealedDossierListings(params),
    staleTime: 30_000,
  });
}
